// ============================================================================
// AdminSideNav — rail lateral de l'ecran Admin (AD-A, P-ADM-8)
// ============================================================================
// Porte du PoC components/admin/admin-side-nav.tsx avec les TOKENS DU PARC
// (jamais les classes shadcn bg-card/text-muted-foreground du PoC). Le PoC a
// 4 sections / 8 onglets ; en vague 1 un seul groupe suffit (7 items — l'onglet
// `reminders` est ABSENT, STOP-11 : differe AD-G avec le mailer ACS, zero lien
// mort). Composant LOCAL gest (releve Fable livrable 7 : 1er usage — promotion
// @gs/ui si un 2e ecran console le reclame). Actif = onglet courant (?tab=).
// Ce module est la SOURCE UNIQUE de la liste des onglets (AdminPage la consomme).

import { Link } from 'react-router-dom';
import {
  Bell, ClipboardList, FileText, LayoutDashboard, ScrollText, Table2, Users, Wallet,
  type LucideIcon,
} from 'lucide-react';
import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';

/**
 * Onglets de l'ecran Admin. `reminders` (8e onglet) etait VOLONTAIREMENT absent
 * en vague 1 (STOP-11 : differe tant que le mailer ACS n'existait pas) ; AD-G
 * l'ajoute (1er consommateur de @gs/mailer, FRAG R merge). Sa cle i18n
 * `gest:admin.tabs.reminders` est indirecte (t(`...${tab}`)) -> manual-keys.ts.
 */
export const ADMIN_TAB_KEYS = [
  'overview', 'members', 'teams', 'remuneration', 'contrats', 'questions', 'reminders', 'audit',
] as const;
export type AdminTab = (typeof ADMIN_TAB_KEYS)[number];

const TAB_ICONS: Record<AdminTab, LucideIcon> = {
  overview: LayoutDashboard,
  members: Users,
  teams: Table2,
  remuneration: Wallet,
  contrats: FileText,
  questions: ClipboardList,
  reminders: Bell,
  audit: ScrollText,
};

/** Lien d'onglet : ?tab= omis pour le defaut `overview` (URL canonique courte). */
function hrefFor(tab: AdminTab): string {
  return tab === 'overview' ? '/admin' : `/admin?tab=${tab}`;
}

export function AdminSideNav({ active }: { active: AdminTab }) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t('gest:admin.navLabel')}
      className={`${GS_COMPONENTS.card} flex gap-1 overflow-x-auto p-2 md:w-56 md:shrink-0 md:flex-col`}
    >
      {ADMIN_TAB_KEYS.map((tab) => {
        const isActive = tab === active;
        const Icon = TAB_ICONS[tab];
        return (
          <Link
            key={tab}
            to={hrefFor(tab)}
            aria-current={isActive ? 'page' : undefined}
            className={`flex shrink-0 touch-manipulation items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-field transition-colors ${
              isActive
                ? 'bg-brand-50 font-medium text-brand-600 dark:bg-brand-950 dark:text-brand-400'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Icon size={16} aria-hidden="true" className="shrink-0" />
            <span>{t(`gest:admin.tabs.${tab}`)}</span>
          </Link>
        );
      })}
    </nav>
  );
}
